import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { consts } from '../../../consts';
import { ActualSelected, Utils } from '../../../shared/utils';
import { Efectivo, IngresoACaja } from '../../../models/finanzas/efectivo/efectivo.model';
import { EfectivoService } from '../../../services/finanzas/efectivo/efectivo.service';
import { ActivityService } from '../../user/activity/activity.service';
import { MasterService } from '../../../modules_v2/master/services/master.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import {
  bankAccountOptions,
  currentActivityOption,
  EntityFormOption,
  fromApiDate,
  InjectedOptions,
  optionId,
} from '../../../modules_v2/finance/entity-form/entity-form.utils';

export interface EfectivoDialogIngresoACajaData {
  efectivo: Efectivo;
  editUid?: string;
}

@Component({
    selector: 'convfact-efectivoingresoacaja',
    templateUrl: './efectivo-dialog-ingreso-a-caja.component.html',
    styleUrls: ['./efectivo-dialog.component.css'],
    standalone: false
})
export class EfectivoDialogIngresoACajaComponent implements OnInit {

  public efectivo: Efectivo;

  public ready = false;
  public loadingList = false;
  public loadError = false;
  public saving = false;
  public savingMessage = '';
  public submitted = false;
  public saveError = '';

  public editing: IngresoACaja | null = null;

  public bankAccounts: EntityFormOption[] = [];
  public activities: EntityFormOption[] = [];
  public dataSource: IngresoACaja[] = [];

  public readonly year = ActualSelected.yearSelected;

  public form: UntypedFormGroup;

  private model: IngresoACaja = this.emptyIngreso();
  private changesAtModal = false;
  private requestedEditDone = false;
  private readonly injected = new InjectedOptions();

  static open(dialog: MatDialog, data: EfectivoDialogIngresoACajaData): Observable<boolean> {
    return dialog.open<EfectivoDialogIngresoACajaComponent, EfectivoDialogIngresoACajaData, boolean>(EfectivoDialogIngresoACajaComponent, {
      width: '720px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<EfectivoDialogIngresoACajaComponent, boolean>,
    public efectivoService: EfectivoService,
    @Inject(MAT_DIALOG_DATA) public data: EfectivoDialogIngresoACajaData,
    public activityService: ActivityService,
    private masterService: MasterService,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      fecha: [new Date()],
      concepto: [''],
      importe: [null],
      comisiones: [null],
      bankAccount: [null],
      activity: [null],
    });
  }

  get isEditMode(): boolean {
    return !!this.editing;
  }

  get subtitle(): string {
    const parts = [this.efectivo?.nombre, this.efectivo?.accountingModelsNumber ? 'Cuenta ' + this.efectivo.accountingModelsNumber : null];
    return parts.filter(Boolean).join(' · ');
  }

  get totalImporte(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }

  get totalComisiones(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.comisiones) || 0), 0));
  }

  get conceptoLength(): number {
    return String(this.form.get('concepto').value || '').length;
  }

  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get missingImporte(): boolean { return !Number(this.form.get('importe').value); }
  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }
  get missingBankAccount(): boolean { return optionId(this.form.get('bankAccount').value) === null; }

  public control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  public ngOnInit(): void {
    this.efectivo = this.data.efectivo;
    this.load();
  }

  public retry(): void {
    this.loadError = false;
    this.load();
  }

  public save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';

    if (this.missingDate || this.missingConcept || this.missingImporte || this.missingActivity || this.missingBankAccount) {
      return;
    }

    const wasEdit = this.isEditMode;
    const payload = this.buildPayload();

    this.changesAtModal = true;
    this.saving = true;
    this.savingMessage = wasEdit ? 'Actualizando ingreso…' : 'Añadiendo ingreso…';
    this.dialogRef.disableClose = true;

    const request = wasEdit
      ? this.efectivoService.ingresoACajaUpdate(payload)
      : this.efectivoService.ingresoACajaCreate(payload);

    request
      .then(() => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.ok(wasEdit ? 'Ingreso a caja actualizado correctamente' : 'Ingreso a caja añadido correctamente');
        if (!wasEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.resetForm();
        this.reloadList();
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = (error && error.error && error.error.message)
          || (wasEdit ? 'Ha ocurrido un error inesperado al actualizar el ingreso de banco' : 'Ha ocurrido un error inesperado al añadir el ingreso de banco');
      });
  }

  public delete(item: IngresoACaja): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar ingreso a caja',
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará el ingreso y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando ingreso…',
      errorText: 'Ha ocurrido un error al eliminar el ingreso de banco',
      action: () => this.efectivoService.ingresoACajaDelete(item.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changesAtModal = true;
      this.notify.ok('Ingreso a caja eliminado correctamente');
      if (this.editing && this.editing.uid === item.uid) this.resetForm();
      this.reloadList();
    });
  }

  public edit(item: IngresoACaja): void {
    if (this.saving) return;
    this.editing = item;
    this.submitted = false;
    this.saveError = '';
    this.injected.clear();

    this.model = { ...item };

    this.form.patchValue({
      fecha: fromApiDate(item.fecha),
      concepto: item.concepto || '',
      importe: item.importe != null ? Number(item.importe) : null,
      comisiones: item.comisiones != null ? Number(item.comisiones) : null,
      bankAccount: this.injected.ensure(this.bankAccounts, item.bankAccountsUid, item.bankAccountInfo),
      activity: this.injected.ensure(this.activities, item.activitiesUid, item.activitiesName),
    }, { emitEvent: false });
  }

  public cancelEdit(): void {
    this.resetForm();
  }

  public closeDialog(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changesAtModal);
  }

  public trackByUid(_: number, item: IngresoACaja): string { return item.uid; }

  private load(): void {
    this.ready = false;
    Promise.all([
      this.loadActivities(),
      this.loadBankAccounts(),
      this.listRequest(),
    ])
      .then(([activities, bankAccounts, list]) => {
        this.activities = activities;
        this.bankAccounts = bankAccounts;
        this.dataSource = list;
        this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private reloadList(): void {
    this.loadingList = true;
    this.listRequest()
      .then(list => {
        this.dataSource = list;
        this.openRequestedEdit();
      })
      .catch(() => { this.notify.error('No se pudieron recargar los ingresos de la caja'); })
      .then(() => { this.loadingList = false; });
  }

  private listRequest(): Promise<IngresoACaja[]> {
    return this.efectivoService.ingresoACajaList(this.efectivo.uid)
      .then(response => Array.isArray(response) ? response : []);
  }

  private loadActivities(): Promise<EntityFormOption[]> {
    return this.activityService.getActiveList()
      .then(list => (list || [])
        .filter(activity => activity && activity.uid && activity.uid !== consts.EMPTY_GUID)
        .map(activity => ({ id: activity.uid, display: activity.name || '' })))
      .catch(() => [] as EntityFormOption[]);
  }

  private loadBankAccounts(): Promise<EntityFormOption[]> {
    return firstValueFrom(this.masterService.getBankAccounts())
      .then(list => bankAccountOptions(list))
      .catch(() => [] as EntityFormOption[]);
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const row = this.dataSource.find(item => item.uid === this.data.editUid);
    if (row) this.edit(row);
  }

  private resetForm(): void {
    this.editing = null;
    this.injected.clear();
    this.submitted = false;
    this.saveError = '';
    this.model = this.emptyIngreso();
    this.form.reset({
      fecha: new Date(),
      concepto: '',
      importe: null,
      comisiones: null,
      bankAccount: null,
      activity: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;

    if (this.missingActivity) {
      const current = currentActivityOption(this.activities);
      if (current) this.control('activity').setValue(current, { emitEvent: false });
      else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
    }
    if (this.missingBankAccount && this.bankAccounts.length === 1) {
      this.control('bankAccount').setValue(this.bankAccounts[0], { emitEvent: false });
    }
  }

  private buildPayload(): IngresoACaja {
    const value = this.form.getRawValue();
    return {
      ...this.model,
      efectivoId: this.efectivo.uid,
      fecha: value.fecha,
      concepto: String(value.concepto || '').trim(),
      importe: Number(value.importe) || 0,
      comisiones: Number(value.comisiones) || 0,
      bankAccountsUid: optionId(value.bankAccount),
      activitiesUid: optionId(value.activity),
    };
  }

  private emptyIngreso(): IngresoACaja {
    return {
      fecha: new Date(),
      concepto: '',
      comisiones: 0,
      importe: 0,
      bankAccountInfo: '',
      bankAccountsUid: consts.EMPTY_GUID,
      uid: undefined,
      efectivoId: '0',
      activitiesUid: consts.EMPTY_GUID,
      activitiesName: '',
    };
  }

  private details(item: IngresoACaja): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (item.fecha) details.push({ label: 'Fecha', value: formatDate(item.fecha, 'dd/MM/yyyy', 'es-ES') });
    if (item.concepto) details.push({ label: 'Concepto', value: item.concepto });
    if (item.bankAccountInfo) details.push({ label: 'Banco', value: item.bankAccountInfo });
    details.push({ label: 'Importe', value: Utils.FormatMoney(Number(item.importe) || 0) + ' €' });
    return details;
  }
}
